import axios from "axios";
import { toast } from "sonner";
import { broadcastExportIntent } from "@/lib/exportIntent";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// v77.99.73 — `withCredentials: true` is required so the browser
// auto-sends our httpOnly `cpc_token` cookie + non-httpOnly
// `cpc_csrf` cookie. Without it the browser strips both.
export const api = axios.create({ baseURL: API, withCredentials: true });

// Read a cookie value by name. Used to fish out the non-httpOnly
// `cpc_csrf` cookie for the double-submit pattern.
const readCookie = (name) => {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : null;
};

const SAFE_METHODS = new Set(["get", "head", "options"]);

api.interceptors.request.use((config) => {
  // v77.99.102 — Cookie + CSRF only. The legacy localStorage Bearer
  // fallback has been retired: a JWT in JS-readable storage means any
  // XSS = total session theft, defeating the HttpOnly cookie. The
  // backend continues to accept the `Authorization: Bearer` header
  // for explicit Bearer flows (mediator dashboard, third-party access,
  // integration tests via fetch); those callers must pass the token
  // through `config.headers.Authorization` themselves.
  //
  //   1. The httpOnly `cpc_token` cookie is sent automatically by
  //      the browser (we never touch it from JS).
  //   2. For mutations (POST/PUT/PATCH/DELETE) we mirror the
  //      `cpc_csrf` cookie back as `X-CSRF-Token`. This proves
  //      same-origin: a CSRF attacker can cause the browser to send
  //      the cookies, but the Same-Origin Policy blocks them from
  //      reading the csrf cookie to forge the header.
  const method = (config.method || "get").toLowerCase();
  if (!SAFE_METHODS.has(method)) {
    const csrf = readCookie("cpc_csrf");
    if (csrf) {
      config.headers["X-CSRF-Token"] = csrf;
    }
  }
  return config;
});

// 402 = "upgrade required" — emitted by the backend when a user without an
// active subscription hits a restricted action. v77.99.8 — branches the toast
// label + CTA between the read-only grace period and a fully locked account.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 402) {
      const d = err.response.data?.detail || {};
      const feature = d.feature || "this action";
      const isLocked = d.plan === "locked";
      const isExport = d.scope === "export";
      // Iter 122.trial-pivot — Positive upgrade copy when the gate is
      // an EXPORT (trial users still have full feature access; they
      // just need to subscribe to download). Also signal the topbar
      // TrialPill so it morphs into the matching "Upgrade to
      // download" affordance for ~10s.
      if (isExport) {
        broadcastExportIntent({ feature, ttl_ms: 10000 });
        toast(
          `Upgrade to download ${feature}`,
          {
            description: d.message ||
              `Your work stays here while you decide. Subscribe to unlock downloads, PDFs and exports.`,
            action: {
              label: "Upgrade",
              onClick: () => {
                if (typeof window !== "undefined") window.location.assign("/app/upgrade");
              },
            },
          },
        );
      } else {
        toast.error(
          isLocked ? `Account locked — subscribe to use ${feature}` : `Subscribe to use ${feature}`,
          {
            description: d.message || (isLocked
              ? "Your account is locked. Subscribe to regain full access — your records are safe and on file."
              : "Your free trial has ended. Your records remain safe and available to view — upgrade to record new information."),
            action: {
              label: isLocked ? "Reactivate" : "Subscribe",
              onClick: () => {
                if (typeof window !== "undefined") window.location.assign("/app/upgrade");
              },
            },
          },
        );
      }
    }
    return Promise.reject(err);
  },
);

// v77.99.102 — `cpc_token` is no longer stored in localStorage. These
// helpers are retained as no-ops (and clearToken still actively purges)
// so the (now historical) call-sites compile, and so a pre-v102
// browser tab with a legacy token in storage gets cleaned up on the
// next page load. The browser's HttpOnly `cpc_token` cookie is the
// only place a session token now lives client-side.
export const getToken = () => {
  // Hard-purge any legacy value left over from older releases so XSS
  // can't read it. Returns "" instead of null so callers that string-
  // concatenate the result don't blow up.
  try { localStorage.removeItem("cpc_token"); } catch { /* ignore */ }
  return "";
};
export const setToken = (_t) => {
  // Intentionally a no-op. Logging in (or impersonation) drops the
  // HttpOnly cookie server-side; we don't keep a JS-readable copy.
  try { localStorage.removeItem("cpc_token"); } catch { /* ignore */ }
};
export const clearToken = () => {
  try { localStorage.removeItem("cpc_token"); } catch { /* ignore */ }
};

// One-shot migration: if a legacy token is sitting in storage when
// the app loads, wipe it. Runs once on import.
try { localStorage.removeItem("cpc_token"); } catch { /* ignore */ }
